<script lang="ts">
  import JsApi from '@tencent/tvf-jsapi';
  import ClipboardJS from 'clipboard';
  import { createEventDispatcher, onMount } from 'svelte';

  import { PropertyReceive } from '@/comps-sdk/components/act-common-receive';
  import ButtonV from '@/comps-sdk/components/button-v';
  import { initLotteryCallback } from '@/comps-sdk/components/lottery/extension';
  import Popup from '@/comps-sdk/components/popup';
  import { toast as Toast } from '@/comps-sdk/components/toast';
  import { PRESENT_TYPE } from '@/comps-sdk/constants';
  import { ECommon, ELottery } from '@/comps-sdk/interface';
  import { getCIIDByModID } from '@/comps-sdk/modules/act-utils';
  import { isPropertyDirectRelease } from '@/comps-sdk/services/property-manager/is-property-direct-release';
  import { attaIWAction } from '@/comps-sdk/utils/atta-action-report';
  import { getPlatformId } from '@/comps-sdk/utils/platform';

  import { getReceivePluginData } from './utils';

  const dispatch = createEventDispatcher();

  export let modId: number;
  export let lotteryInfo: any = null;
  export let activityConfig: any = null;
  export let scene = 'iwan';

  let isShowCdkeyDialog = false;
  let cdkeyCode = '';
  let cdkeyLinkUrl = '';

  $: propertyData = lotteryInfo?.property_detail.desc;
  $: orderData = lotteryInfo?.order_detail;
  $: propertyBaseType = +propertyData?.base_type;
  $: propertyPlatId = +propertyData?.delivery_plat.plat_id;
  $: orderStatus = +orderData?.status;
  $: isPhysical = propertyBaseType === ECommon.PropertyBaseType.PHYSICAL;
  $: isVirtual = propertyBaseType === ECommon.PropertyBaseType.VIRTUAL;
  $: isIwanGift = isVirtual && propertyPlatId === ECommon.PlatIdType.WELFARE;

  let iwanGiftInfo: any;
  $: {
    if (!isIwanGift) {
      iwanGiftInfo = null;
    } else {
      try {
        isIwanGift = JSON.parse(propertyData.delivery_plat.extends);
      } catch (e) {
        console.error('parse presentInfo error: ', e);
      }
    }
  }

  $: isShowBtn = !!activityConfig;

  $: presentType = iwanGiftInfo?.gift_present_info.present_type;
  $: isCashWallet = presentType === PRESENT_TYPE.CASH_WALLET;
  $: isCdkey = presentType === PRESENT_TYPE.CDKEY;

  // 抽奖爱玩礼包的领取状态
  $: isReceived = isIwanGift && orderStatus === ECommon.OrderStatus.DELIVERED;
  $: isDirectRelease = isPropertyDirectRelease(presentType);
  // cdkey 是否已领取
  $: isCdkeyReceived = isCdkey && isReceived;

  let isBtnDisabled: boolean = true;
  $: {
    // 实物不关心状态
    if (isPhysical) {
      isBtnDisabled = false;
      // 其他类型
    } else if (orderStatus === ECommon.OrderStatus.UNDELIVERED) {
      isBtnDisabled = isDirectRelease;
    } else if (orderStatus === ECommon.OrderStatus.DELIVERED && isIwanGift) {
      isBtnDisabled = !(presentType === PRESENT_TYPE.CDKEY || presentType === PRESENT_TYPE.CASH_WALLET);
    }
  }

  let btnText: string;
  $: {
    if (isPhysical) {
      btnText = '填地址';
    } else {
      if (orderStatus === ECommon.OrderStatus.UNDELIVERED) {
        btnText = isDirectRelease ? '未发货' : '去领取';
      } else if (orderStatus === ECommon.OrderStatus.DELIVERED) {
        if (isCdkey) {
          btnText = '去兑换';
        } else if (isCashWallet) {
          btnText = '去提现';
        } else {
          btnText = '已发货';
        }
      } else {
        btnText = '发货失败';
      }
    }
  }

  const handleClick = async () => {
    const lotteryId = await getCIIDByModID(modId);
    console.log(
      '[comps-sdk:award-list-drawer:handleClick]',
      `lotteryId: ${lotteryId}`,
      `isPhysical: ${isPhysical}`,
      `isIwanGift: ${isIwanGift}`,
      `isCashWallet: ${isCashWallet}`,
      `isCdkey: ${isCdkey}`,
      `isReceived: ${isReceived}`,
      `isCdkeyReceived: ${isCdkeyReceived}`,
      'iwanGiftInfo: ',
      iwanGiftInfo,
    );

    // 需要根据不同物品或礼包类型执行不同的逻辑
    if (isPhysical) {
      // 实物直接调用领取插件
      initLotteryCallback({
        type: ELottery.PrizeType.good,
        data: {
          presentInfo: null,
          modId,
          lotteryIID: lotteryId,
          property_results: [lotteryInfo],
          orderId: orderData.order_id,
          platformId: getPlatformId(),
          userType: Number(activityConfig.main_account_type) - 1,
        },
      });
      dispatch('close-drawer');
      return;
    }

    // 爱玩礼包
    if (isIwanGift) {
      // 礼包 cdkey需要根据领取状态来区分逻辑，其他的可点击都是领取
      if (isCdkey) {
        if (isReceived) {
          // 已领取则弹出查看弹窗
          handleViewCdkeyCode();
          dispatch('close-drawer');
          return;
        }

        // 未领取调用插件
        const pluginData = {
          type: ELottery.PrizeType.cdkey,
          data: {
            presentInfo: iwanGiftInfo,
            modId,
            lotteryIID: lotteryId,
            property_results: [lotteryInfo],
            orderId: orderData.order_id,
            platformId: getPlatformId(),
            userType: Number(activityConfig.main_account_type) - 1,
          },
        };

        // 调用cdkey领取插件
        initLotteryCallback(pluginData);
        dispatch('close-drawer');
        return;
      }

      // 现金红包
      if (isCashWallet && isReceived) {
        // 现金红包已经发货，展示去提现，跳转提现接口
        const withDrawUrl = iwanGiftInfo?.gift_present_info?.ext.convert_link || '';
        if (withDrawUrl) {
          JsApi.invoke('openUrl', {
            url: withDrawUrl,
          });
        }
        return;
      }

      // 其他类型全部调用领取插件
      try {
        const pluginData = getReceivePluginData({
          modId,
          presentType,
          lotteryIID: lotteryId,
          lotteryInfo,
          iwanGiftInfo,
          activityConfig,
        });
        initLotteryCallback(pluginData, scene as any);
        dispatch('close-drawer');
      } catch (e: any) {
        Toast(e.message);
        // 上报原始 Error
        attaIWAction({
          actionType: 'award-list-drawer-lottery-initLotteryCallback-error',
          extraJson: JSON.stringify({
            modId,
            lotteryIID: lotteryId,
            orderId: orderData.order_id,
            ...e,
            message: e?.message || '',
          }),
        });
      }
    }
  };

  const handleViewCdkeyCode = () => {
    cdkeyCode = orderData?.delivery_info?.cdkeyCode || '';
    cdkeyLinkUrl = iwanGiftInfo?.gift_present_info?.ext.convert_link || '';

    const {
      instance_info: instanceInfo = {},
      property_detail: propertyDetail = {},
      order_detail: orderDetail = {},
    } = lotteryInfo;

    const title = `${instanceInfo?.name || ''}`;

    new PropertyReceive({
      propertyData: {
        instanceInfo,
        propertyDetail,
        orderDetail,
      },
      showResult: {
        title,
        desc: '',
        customData: {
          cdkeyCode,
          cdkeyLink: cdkeyLinkUrl,
        },
        btnText: '前往兑换',
      },
      isReceived: true,
    });
  };

  const handleCdkeyCode = () => {
    if (cdkeyLinkUrl) {
      JsApi.invoke('openUrl', {
        url: cdkeyLinkUrl,
      });
    } else {
      isShowCdkeyDialog = false;
    }
  };

  let clipboard;
  onMount(() => {
    clipboard = new ClipboardJS('.lottery-action-button-copy-cdkey');
    clipboard.on('success', () => {
      Toast('复制成功');
    });
  });
</script>

{#if isShowBtn}
  <div class={`act-sdk-lottery-action-btn ${$$restProps.class || ''}`}>
    <ButtonV class={`btn ${isBtnDisabled ? 'disabled' : ''}`} disabled={isBtnDisabled} on:click={handleClick}>
      <span>{btnText}</span>
    </ButtonV>

    <!--只有cdkey礼包且已领取情况下才弹出查看cdkey码弹窗 !-->
    {#if isCdkeyReceived}
      <Popup bind:value={isShowCdkeyDialog} round closeable get-container="body" class="cdkey-popup">
        <div class="popup-title">兑换成功</div>
        <div style="margin: 8px">激活码:</div>
        <div class="cdkey-code">
          {cdkeyCode}
          {#if cdkeyCode}
            <ButtonV class="lottery-action-button-copy-btn">
              <span data-clipboard-text={cdkeyCode} class="lottery-action-button-copy-cdkey">复制</span>
            </ButtonV>
          {/if}
        </div>
        <div class="cdkey-btn-wrapper">
          <ButtonV class="cdkey-btn" on:click={handleCdkeyCode}>
            {cdkeyLinkUrl ? '前往兑换' : '确定'}
          </ButtonV>
        </div>
      </Popup>
    {/if}
  </div>
{/if}

<style lang="scss">
  :global(.act-sdk-lottery-action-btn) {
    .btn {
      background-color: #ffee45;
      color: #9b7405;
      height: 34px;
      width: 100px;
      border-radius: 24px;
      border: none;
    }

    .disabled {
      background-color: rgba(0, 0, 0, 0.6);
      color: #fff;
    }

    .cdkey-popup {
      padding: 16px 20px;
      width: 260px;
      min-height: 160px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
    }

    .cdkey-code {
      color: rgb(241, 212, 64);
      font-size: 14px;
      line-height: 20px;
      margin: 5px 0px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .btn-copy {
      display: inline-block;
      margin-left: 20px;
    }
    .cdkey-btn-wrapper {
      margin-top: 10px;
      margin-bottom: 5px;
      :global(.cdkey-btn) {
        background-color: #ffee45;
        color: #9b7405;
        height: 34px;
        width: 100px;
        border-radius: 24px;
        display: inline-block;
        line-height: 34px;
        text-align: center;
        border: none;
      }
    }

    .popup-title {
      font-size: 18px;
      margin-bottom: 10px;
    }

    .account-intro {
      font-size: 12px;
      color: #7a7a7a;
      margin: 5px 0px;
      text-align: center;
    }
    .lottery-action-button-copy-btn {
      border: none;
      display: inline-flex;
      align-items: center;
      line-height: 1;
      white-space: nowrap;
      box-sizing: border-box;
      justify-content: center;
      border-radius: 6px;
      font-family: PingFangSC-Regular;
      font-size: 14px;
      color: #8c6e06;
      background: #fff3c3;
      line-height: 20px;
      width: 50px;
      height: 30px;
      margin-left: 10px;
    }
  }
</style>
